package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.DerivedAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.NamespacedStateAst
import top.wcpe.allincore.dsl.compiler.ast.ParamsAst
import top.wcpe.allincore.dsl.compiler.ast.StateAst
import top.wcpe.allincore.dsl.compiler.ast.TypeAst
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * 解析 `state defaults { name [: Type] = literal ... }` 块。
 *
 * 仅接受字面量值；类型标注可选，缺省时等价于 AnyT。
 *
 * M6 review must-fix A：放宽默认值字面量集合到
 *  - 基础字面量：String / Number / Boolean
 *  - `null`
 *  - 列表字面量 `[a, b, ...]`（元素必须递归仍是字面量）
 *  - Map 字面量 `#{"k": v, ...}`（每个值必须递归仍是字面量）
 *
 * 但严禁出现表达式（IdentRef / 运算 / 函数调用 等）；递归字面量校验在
 * [requireLiteralRecursive] 实现。
 *
 * 重复字段不在 parse 阶段拒绝，交由 semantic 阶段（ScopeBuilder）统一上报 DSL_E101。
 */
internal class StateBlockParser(private val p: Parser) {

    fun parse(): StateAst {
        val stateTok = p.expect(TokenType.KW_STATE)
        p.expect(TokenType.KW_DEFAULTS, "state 后必须跟 defaults")
        p.expect(TokenType.LBRACE, "state defaults 缺少 '{'")
        p.skipNewlines()

        val entries = parseEntries("state")

        val close = p.expect(TokenType.RBRACE, "state defaults 缺少 '}'")
        return StateAst(entries = entries, position = p.completePosition(stateTok.position, close))
    }

    /**
     * 解析 `<scope> defaults { ... }`，scope ∈ session / persist。
     *
     * 与 [parse] 完全同形态，仅替换头部关键字与诊断文案。
     */
    fun parseScopedDefaults(scopeToken: TokenType, scopeName: String): StateAst {
        val tok = p.expect(scopeToken)
        p.expect(TokenType.KW_DEFAULTS, "$scopeName 后必须跟 defaults")
        p.expect(TokenType.LBRACE, "$scopeName defaults 缺少 '{'")
        p.skipNewlines()

        val entries = parseEntries(scopeName)

        val close = p.expect(TokenType.RBRACE, "$scopeName defaults 缺少 '}'")
        return StateAst(entries = entries, position = p.completePosition(tok.position, close))
    }

    /**
     * 解析 `derived defaults { name [: Type] = expr ... }` 块。
     *
     * 与 [parse] 同形态，但条目值是**完整表达式**（不做 literal 限制），直接复用
     * [ExpressionParser]。表达式合法性（禁止赋值 / await、循环依赖）交由语义层校验。
     */
    fun parseDerived(): DerivedAst {
        val tok = p.expect(TokenType.KW_DERIVED)
        p.expect(TokenType.KW_DEFAULTS, "derived 后必须跟 defaults")
        p.expect(TokenType.LBRACE, "derived defaults 缺少 '{'")
        p.skipNewlines()

        val entries = parseDerivedEntries()

        val close = p.expect(TokenType.RBRACE, "derived defaults 缺少 '}'")
        return DerivedAst(entries = entries, position = p.completePosition(tok.position, close))
    }

    /** 解析 v2 `state { ... }`。 */
    fun parseModelState(): StateAst {
        val stateTok = p.expect(TokenType.KW_STATE)
        p.expect(TokenType.LBRACE, "state 缺少 '{'")
        p.skipNewlines()
        val entries = parseEntries("state")
        val close = p.expect(TokenType.RBRACE, "state 缺少 '}'")
        return StateAst(entries = entries, position = p.completePosition(stateTok.position, close))
    }

    /** 解析 v2 `session namespace { ... }` / `persist namespace { ... }`。 */
    fun parseModelScopedState(scopeToken: TokenType, scopeName: String): NamespacedStateAst {
        val tok = p.expect(scopeToken)
        val namespace = p.expect(TokenType.IDENTIFIER, "$scopeName 后缺少 namespace")
        p.rejectReservedUserIdentifier(namespace.lexeme, namespace.position)
        p.expect(TokenType.LBRACE, "$scopeName ${namespace.lexeme} 缺少 '{'")
        p.skipNewlines()
        val entries = parseEntries(scopeName)
        val close = p.expect(TokenType.RBRACE, "$scopeName ${namespace.lexeme} 缺少 '}'")
        val position = p.completePosition(tok.position, close)
        return NamespacedStateAst(
            namespace = namespace.lexeme,
            state = StateAst(entries = entries, position = position),
            position = position,
        )
    }

    /** 解析 v2 `derived { ... }`。 */
    fun parseModelDerived(): DerivedAst {
        val tok = p.expect(TokenType.KW_DERIVED)
        p.expect(TokenType.LBRACE, "derived 缺少 '{'")
        p.skipNewlines()
        val entries = parseDerivedEntries()
        val close = p.expect(TokenType.RBRACE, "derived 缺少 '}'")
        return DerivedAst(entries = entries, position = p.completePosition(tok.position, close))
    }

    /** 解析 v2 `params { name: Type [= literal] }`。 */
    fun parseParams(): ParamsAst {
        val tok = p.expect(TokenType.IDENTIFIER, "params 缺失")
        p.expect(TokenType.LBRACE, "params 缺少 '{'")
        p.skipNewlines()
        val entries = mutableListOf<ParamsAst.ParamEntry>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val nameTok = p.expect(TokenType.IDENTIFIER, "params 字段名缺失")
            p.rejectReservedUserIdentifier(nameTok.lexeme, nameTok.position)
            p.expect(TokenType.COLON, "params 字段 '${nameTok.lexeme}' 后缺少类型")
            val type = TypeParser.parse(p)
            val defaultValue = if (p.check(TokenType.EQUALS)) {
                p.advance()
                parseLiteral()
            } else null
            entries += ParamsAst.ParamEntry(nameTok.lexeme, type, defaultValue, nameTok.position)
            if (!p.check(TokenType.RBRACE)) {
                p.expect(TokenType.NEWLINE, "params 字段后缺少换行")
                p.skipNewlines()
            }
        }
        val close = p.expect(TokenType.RBRACE, "params 缺少 '}'")
        return ParamsAst(entries, p.completePosition(tok.position, close))
    }

    private fun parseDerivedEntries(): List<DerivedAst.DerivedEntry> {
        val entries = mutableListOf<DerivedAst.DerivedEntry>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val nameTok = p.expect(TokenType.IDENTIFIER, "derived 字段名缺失")
            p.rejectReservedUserIdentifier(nameTok.lexeme, nameTok.position)
            val name = nameTok.lexeme
            val type: TypeAst? = if (p.check(TokenType.COLON)) {
                p.advance()
                TypeParser.parse(p)
            } else null
            p.expect(TokenType.EQUALS, "derived 字段 '$name' 后缺少 '='")
            val expr = ExpressionParser(p).parse()
            entries += DerivedAst.DerivedEntry(
                name = name,
                expr = expr,
                position = nameTok.position,
                type = type,
            )

            if (!p.check(TokenType.RBRACE)) {
                p.expect(TokenType.NEWLINE, "derived 字段后缺少换行")
                p.skipNewlines()
            }
        }
        return entries
    }

    /** 解析字段列表（state / session / persist defaults 共用）。scopeName 仅用于诊断文案。 */
    private fun parseEntries(scopeName: String): List<StateAst.StateEntry> {
        val entries = mutableListOf<StateAst.StateEntry>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val nameTok = p.expect(TokenType.IDENTIFIER, "$scopeName 字段名缺失")
            p.rejectReservedUserIdentifier(nameTok.lexeme, nameTok.position)
            val name = nameTok.lexeme
            // M5-c #I5：可选类型标注 `: Type`。写了才进入 wire 与 runtime 校验。
            val type: TypeAst? = if (p.check(TokenType.COLON)) {
                p.advance()
                TypeParser.parse(p)
            } else null
            p.expect(TokenType.EQUALS, "$scopeName 字段 '$name' 后缺少 '='")
            val literal = parseLiteral()
            entries += StateAst.StateEntry(
                name = name,
                literal = literal,
                position = nameTok.position,
                type = type,
            )

            // 行尾：要么 NEWLINE，要么直接 RBRACE
            if (!p.check(TokenType.RBRACE)) {
                p.expect(TokenType.NEWLINE, "$scopeName 字段后缺少换行")
                p.skipNewlines()
            }
        }
        return entries
    }

    /**
     * 解析 state 默认值字面量。
     *
     * 解析流程：复用 [ExpressionParser] 解析得到完整表达式节点，再交给
     * [requireLiteralRecursive] 强制只允许字面量形态（含 List / Map / null）。
     * 这样的好处是：
     *  - 表达式形态（如 `100 + 1`）在静态期被识别为 BinaryOp，能给出精确诊断；
     *  - List / Map 容器内部由同一份递归校验兜底，不为单次使用引入抽象。
     */
    private fun parseLiteral(): ExprAst {
        val expr = ExpressionParser(p).parse()
        requireLiteralRecursive(expr)
        return expr
    }

    /**
     * 递归校验 [expr] 仅由字面量构成。
     *
     * 允许：StringLit / NumberLit / BoolLit / NullLit / ListLiteralExpr / MapLiteralExpr，
     * 且容器内部元素本身也必须满足同样约束。出现任何表达式形态（IdentRef、StateRef、
     * BinaryOp、CallExpr 等）时立即 [Parser.fail]。
     */
    private fun requireLiteralRecursive(expr: ExprAst) {
        when (expr) {
            is ExprAst.Literal -> Unit
            is ExprAst.NullLit -> Unit
            is ExprAst.ListLiteralExpr -> expr.elements.forEach(::requireLiteralRecursive)
            is ExprAst.MapLiteralExpr -> expr.entries.forEach { requireLiteralRecursive(it.value) }
            else -> p.fail(
                "state 默认值只接受字面量，禁止出现表达式（${expr::class.simpleName}）",
                expr.position,
            )
        }
    }
}
